// https://github.com/enisdenjo/graphql-ws
// TODO :: https://app.shortcut.com/qumulex/story/28667/implement-graphql-ws-package-in-client
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient as createWsClient } from "graphql-ws";
import { getMainDefinition } from '@apollo/client/utilities';
import { Operation } from '@apollo/client/core';
import { split } from '@apollo/client/core';
import { getOperationAST } from 'graphql';
import { CreateApolloClientOptions, AddHeadersMethod } from './types';

type ConnectionParamsFormatter = (headers: Record<string, string>) => Record<string, unknown>;

/**
 * Used as the first argument in `split` to determine
 * if the operation is a subscription.
 */
export const isSubscription = ({ query }: Operation): boolean => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
};

/**
 * Hard-coded remote schema for use with
 * the heartbeat subscription.
 */
const remoteSchema = {
  queries: [],
  mutations: [],
  /** Remote GraphQL subscriptions (not Hasura). Must match root subscription field name. */
  subscriptions: ['heartbeat', 'liveEvents'],
};

/**
 * Hasura WS format: JWT nested under `headers` with `Authorization` key.
 * See: https://hasura.io/docs/latest/auth/authentication/jwt/
 */
const hasuraConnectionParams: ConnectionParamsFormatter = (headers) => ({
  headers: { ...headers, authToken: headers['Authorization'] },
});

/**
 * Remote schema gateway format: raw JWT (no "Bearer " prefix) in top-level `authToken`.
 * Matches the pattern used by VideoPlayer's `signaling-server-auth-token`.
 */
const remoteConnectionParams: ConnectionParamsFormatter = (headers) => ({
  authToken: headers['Authorization']?.replace(/^Bearer\s+/i, '') ?? '',
});

export const createApolloClientSubscriptionsLink = (options: CreateApolloClientOptions) => {

  const wsLink = createSubscriptionLink(options.wsEndpoint, options.addHeaders, hasuraConnectionParams);
  const wsRemoteLink = createSubscriptionLink(options.wsRemoteSchemaEndpoint, options.addRemoteHeaders, remoteConnectionParams);


  // handle relay schema subscriptions
  const wsRelayUrl = options.wsEndpoint?.replace('v1/graphql', 'v1beta1/relay');
  const wsRelayLink = createSubscriptionLink(wsRelayUrl, options.addHeaders, hasuraConnectionParams);

  /**
   * Determine if we should use the
   * Relay schema for subscription
   */
  const relaySplitLink = split(
    (operation) => {
      const operationAST = getOperationAST(operation.query, operation.operationName);
      const queryName = (operationAST?.selectionSet?.selections?.[0] as { name?: { value?: string } })?.name?.value;
      return queryName?.includes('_connection') ?? false;
    },
    wsRelayLink, // goes through hasura
    wsLink // goes through hasura
  );

  const dualWebSocketLink = split(
    // split based on subscription destination
    (operation) => {
      const operationAST = getOperationAST(operation.query, operation.operationName);
      const subsName = (operationAST?.selectionSet?.selections?.[0] as { name?: { value?: string } })?.name?.value;
      return remoteSchema.subscriptions.indexOf(subsName ?? '') > -1;
    },
    wsRemoteLink, // heartbeat, does not go through hasura
    relaySplitLink
  );

  return dualWebSocketLink
}

function createSubscriptionLink(endpoint: string, addHeaders: AddHeadersMethod, formatConnectionParams: ConnectionParamsFormatter) {

  const wsClient = createWsClient({
    url: endpoint,
    lazy: true,
    retryAttempts: 3,
    connectionParams: async () => {
      const headers = await addHeaders();

      if ('Authorization' in headers) {
        return formatConnectionParams(headers);
      }

      return { headers };
    },
  });

  const wsLink = new GraphQLWsLink(wsClient)

  return wsLink;
}

